import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PhoneIcon from '@mui/icons-material/Phone';
import { Divider, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ResearchResult, StoredLeadList } from 'services/lead-list-research.service';
import { makeStyles } from 'tss-react/mui';

interface Props {
  data: StoredLeadList;
  isEnriching: boolean;
  leads: ResearchResult[];
  handleOpenFullResearchDialog: () => void;
  handleOpenRefinementDialog: () => void;
  handleOpenConfirmDeleteLeadListDialog: () => void;
  handleExportToExcel: () => void;
  handleExportAsCSV: () => void;
  handleEnrichLeadList: () => void;
  handleOpenAddToFunnelDialog: () => void;
  handleStartBetterContactEnrichment: () => void;
  enrichPhoneEmailLabel: string;
  enrichPhoneEmailDisabled: boolean;
  handleRemoveContacts: () => void;
  removeContactsLabel: string;
  removeContactsDisabled: boolean;
}

const LeadListDetailsHeader: React.FC<Props> = ({
  data,
  isEnriching,
  leads,
  handleOpenFullResearchDialog,
  handleOpenRefinementDialog,
  handleOpenConfirmDeleteLeadListDialog,
  handleExportToExcel,
  handleExportAsCSV,
  handleEnrichLeadList,
  handleOpenAddToFunnelDialog,
  handleStartBetterContactEnrichment,
  enrichPhoneEmailLabel,
  enrichPhoneEmailDisabled,
  handleRemoveContacts,
  removeContactsLabel,
  removeContactsDisabled,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={classes.statisticsHeader}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} mb={2}>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <IconButton className={classes.backButton} size={'small'} onClick={() => navigate('/lead-list-storage')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography className={classes.titleText}>{data.name || t('unnamedLeadList')}</Typography>
        </Stack>

        <Stack direction={'row'} gap={1}>
          <PaiButton
            text={t('fullResearch')}
            startIcon={<RocketLaunchIcon />}
            border={'rounded'}
            size={'small'}
            action={handleOpenFullResearchDialog}
          />
          <PaiButton text={t('refineWithAi')} startIcon={<RefreshIcon />} border={'rounded'} size={'small'} action={handleOpenRefinementDialog} />
          <PaiButton
            text={t('delete')}
            startIcon={<DeleteIcon />}
            border={'rounded'}
            size={'small'}
            color={'error'}
            action={handleOpenConfirmDeleteLeadListDialog}
          />
        </Stack>
      </Stack>
      {/* Lead List Info Paper */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: '10px', width: '100%' }}>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Typography className={classes.subTitleText}>{t('product')}</Typography>
            <Typography className={classes.bodyText}>{data.product || t('notSpecified')}</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Typography className={classes.subTitleText}>{t('region')}</Typography>
            <Typography className={classes.bodyText}>{data.region || t('notSpecified')}</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Typography className={classes.subTitleText}>{t('created')}</Typography>
            <Typography className={classes.bodyText}>{new Date(data.createdAt).toLocaleString()}</Typography>
          </Grid>

          {data.metadata?.originalPrompt && (
            <Grid size={12}>
              <Typography className={classes.subTitleText}>{t('originalSearchPrompt')}</Typography>
              <Typography className={classes.bodyText}>&quot;{data.metadata.originalPrompt}&quot;</Typography>
            </Grid>
          )}

          {data.notes && (
            <Grid size={12}>
              <Typography className={classes.subTitleText}>{t('notes')}</Typography>
              <Typography className={classes.bodyText}>{data.notes}</Typography>
            </Grid>
          )}
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
          <Typography className={classes.bodyText}>{t('leadDataCount', { count: leads.length })}</Typography>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <PaiButton
              text={t('exportToExcel')}
              startIcon={<FileDownloadIcon />}
              border={'rounded'}
              size={'small'}
              action={handleExportToExcel}
              disabled={!data?.leadData?.length}
            />
            <PaiButton
              text={t('exportAsCsv')}
              startIcon={<FileDownloadIcon />}
              border={'rounded'}
              size={'small'}
              action={handleExportAsCSV}
              disabled={!data?.leadData?.length}
            />
            <PaiButton
              text={t('addToFunnel')}
              border={'rounded'}
              size={'small'}
              action={handleOpenAddToFunnelDialog}
              disabled={!data?.leadData?.length}
            />
            <PaiButton
              text={t('enrichWithLinkedinUrls')}
              startIcon={<LinkIcon />}
              border={'rounded'}
              size={'small'}
              color={'secondary'}
              action={handleEnrichLeadList}
              disabled={isEnriching || !data?.leadData?.length}
            />
            <PaiButton
              text={enrichPhoneEmailLabel}
              startIcon={<PhoneIcon />}
              border={'rounded'}
              size={'small'}
              color={'secondary'}
              action={handleStartBetterContactEnrichment}
              disabled={enrichPhoneEmailDisabled}
            />
            <PaiButton
              text={removeContactsLabel}
              startIcon={<DeleteIcon />}
              border={'rounded'}
              size={'small'}
              color={'error'}
              action={handleRemoveContacts}
              disabled={removeContactsDisabled}
            />
          </Stack>
        </Stack>
      </Paper>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  statisticsHeader: {
    background: theme.palette.backgrounds.main_new,
    padding: '16px 56px',
    width: '100%',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(24),
  },
  subTitleText: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.text.secondary,
    marginBottom: '4px',
  },
  bodyText: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 'bold',
  },
  backButton: {
    color: theme.palette.text.primary,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
}));

export default LeadListDetailsHeader;
